//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { OmitStrict } from '@utils/types/OmitStrict';

// GENERAL MEDIA TRACKING EVENT TYPES
export type MediaTrackingEvent =
  | IMediaTrackingBaseEvent
  | MediaTrackingEventWithOtherFields;

export type MediaTrackingEventWithOtherFields =
  MediaTrackingMediaShownToUserEvent;

export type MediaTrackingEventComponentLevelData = OmitStrict<
  MediaTrackingQueueEvent,
  'timestamp' | 'source'
>;

export type MediaTrackingQueueEvent = OmitStrict<
  MediaTrackingEvent,
  'sessionId'
>;

export interface IMediaTrackingBaseEvent {
  cuid: string;
  event: MediaTrackingEventName;
  sessionId: string;
  source: MediaTrackingSource;
  timestamp: number;
  fileId?: string;
  videoId?: string;
}

// EVENT SPECIFIC TYPES
// we allow additional fields to be added to the base event using descriminated union types. Each event with additional fields must define
// and "otherFields" property and the specific event the additional fields apply to.
interface IMediaTrackingEventWithOtherFields<
  OtherFields extends Record<string, unknown>,
  EventName extends MediaTrackingEventName,
> extends IMediaTrackingBaseEvent {
  event: EventName;
  otherFields: OtherFields;
}

// This event type supplies the additional fields required by the MediaShownToUser event
export type MediaTrackingMediaShownToUserEvent =
  IMediaTrackingEventWithOtherFields<
    { position: number },
    MediaTrackingEventName.MediaShownToUser
  >;

// EVENT TRACKING ENUMS
export enum MediaTrackingSource {
  CAMPAIGNS_PAGE = 'CampaignsPage',
  INFLUENCER_DETAILS_PAGE = 'InfluencerDetailsPage',
  INFLUENCERS_LIST_MEDIA_PAGE = 'InfluencersListMediaPage',
  INSTAGRAM_HASHTAG_PAGE = 'InstagramHashtagPage',
  INSTAGRAM_MENTIONED_AND_TAGGED_PAGE = 'InstagramMentionedAndTaggedPage',
  NO_SOURCE = 'DefaultValueNoSourceProvided',
}

export enum MediaTrackingEventName {
  AddedToCollection = 'AddedToCollection',
  AddedToGallery = 'AddedToGallery',
  AddedToLibrary = 'AddedToLibrary',
  DistributeClicked = 'DistributeClicked',
  ItemDownloaded = 'ItemDownloaded',
  LabelAdded = 'LabelAdded',
  LabelRemoved = 'LabelRemoved',
  MediaShownToUser = 'MediaShownToUser',
  NoteAdded = 'NoteAdded',
  ProductAdded = 'ProductAdded',
  RemovedFromConnect = 'RemovedFromConnect',
  RequestedReview = 'RequestedReview',
  RevealCreated = 'RevealCreated',
  RightsManuallyApproved = 'RightsManuallyApproved',
  RightsRequested = 'RightsRequested',
  RightsSkipped = 'RightsSkipped',
  ScheduledInstagramFeed = 'ScheduledInstagramFeed',
  ScheduledInstagramStory = 'ScheduledInstagramStory',
  ScheduledInstagramReels = 'ScheduledInstagramReels',
  ScheduledPinterest = 'ScheduledPinterest',
  ScheduledTwitter = 'ScheduledTwitter',
  ScheduledFacebook = 'ScheduledFacebook',
  ScheduledTikTokVideo = 'ScheduledTikTokVideo',
  ScheduledYouTube = 'ScheduledYouTube',
  ShowroomCreated = 'ShowroomCreated',
  UniversalImageActionsModalOpened = 'UniversalImageActionsModalOpened',
}

export interface IMediaTrackingConfig {
  isMediaTrackingEnabled: boolean;
  source: MediaTrackingSource;
  trackVisibility: () => void; // we can add additional methods here depending on the needs of the component
}

export interface IMediaTrackableComponent {
  mediaTrackingConfig?: IMediaTrackingConfig;
}
